import React, {useMemo} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {SearchFormDesktop} from 'components/SearchBox/Home/SearchFormDesktop'
import {SearchFormMobile} from 'components/SearchBox/Home/SearchFormMobile'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {PRIMARY_ROW_MIN_HEIGHT_MOBILE} from '@vio/atlas/NavBar'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {KIWI_CUSTOM_HEADER_HEIGHT_MOBILE} from '@vio/shared/src/whiteLabel/CustomHeader'
import {PoweredBy} from '@vio/shared/src/whiteLabel/PoweredBy'

import {AlternativeDesktopSearchForm} from './AlternativeDesktopSearchForm'

const Wrapper = styled.section<{backgroundImage?: string}>`
  position: relative;
  padding-top: ${PRIMARY_ROW_MIN_HEIGHT_MOBILE};
  &.--has-custom-header {
    padding-top: 0;
  }
  &.--has-mobile-hero-bg {
    margin-top: -${KIWI_CUSTOM_HEADER_HEIGHT_MOBILE}px;
    padding-top: ${KIWI_CUSTOM_HEADER_HEIGHT_MOBILE}px;
    background: ${({backgroundImage}) =>
      `url(${backgroundImage}) right top no-repeat`};

    ${linariaMq.desktopXs} {
      margin-top: 0;
      padding-top: 0;
      background: none;
    }
  }
  &.--has-mobile-hero-cover {
    background: ${({backgroundImage}) =>
      `url(${backgroundImage}) 20% center / cover no-repeat content-box`};
  }
`

const SearchFormMobileWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  position: relative;
  padding-top: ${cssTheme.layout.spacing.s500};
  padding-bottom: ${cssTheme.layout.spacing.s600};
  padding-left: ${cssTheme.layout.spacing.s400};
  padding-right: ${cssTheme.layout.spacing.s400};

  ${linariaMq.desktopXs} {
    max-width: 667px;
  }

  & > div {
    width: inherit;
    padding: ${cssTheme.layout.spacing.s400};
    border-radius: ${cssTheme.layout.radius.xl};
    box-shadow: ${cssTheme.shadows.floating};
  }
`

const SearchFormDesktopWrapper = styled.div`
  .--search-form-horizontal-grid {
    ${linariaMq.desktopSm} {
      width: 800px;
      background-color: ${cssTheme.colors.background.neutral.primary};
      border: solid 1px ${cssTheme.colors.border.neutral.secondary};
      box-shadow: ${cssTheme.shadows.floating};
    }
  }
`

const PoweredByWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: ${cssTheme.layout.spacing.s300};
`

export const HeroSectionNonVio: React.FC = () => {
  const {brandHomepageHeadingMessage, brandHomepageSubheadingMessage} =
    useBrand()
  const {shell, commerce} = useBrandCapabilities()
  const isCoBranded = commerce.coBranded.enabled
  const {hasCustomHeader, compactHeroMobileImage} = shell
  const heroImgName = compactHeroMobileImage
    ? `${process.env.NEXT_PUBLIC_ASSETS_ENDPOINT}static/${compactHeroMobileImage}`
    : ''
  const alternativeForm = shell.hasAlternativeSearchForm.enabled
    ? shell.hasAlternativeSearchForm.config
    : undefined
  const mobileCoverImage =
    alternativeForm?.hasMobileHeroImage && alternativeForm.heroImage
      ? `${process.env.NEXT_PUBLIC_ASSETS_ENDPOINT}static/${alternativeForm.heroImage}`
      : ''
  const heroContentColorPath = mobileCoverImage
    ? 'homepage.hero.content'
    : undefined
  const {isMobile, isDesktopXs} = useDeviceLayout()

  const title = useMemo(() => {
    if (brandHomepageHeadingMessage)
      return <FormattedMessageWrapper {...brandHomepageHeadingMessage} />

    return null
  }, [brandHomepageHeadingMessage])

  const subtitle = useMemo(() => {
    if (shell.hasHomeSubheading && brandHomepageSubheadingMessage)
      return <FormattedMessageWrapper {...brandHomepageSubheadingMessage} />

    return null
  }, [shell.hasHomeSubheading, brandHomepageSubheadingMessage])

  if (isMobile)
    return (
      <Wrapper
        data-id="HeroSection"
        backgroundImage={mobileCoverImage || heroImgName}
        className={cx(
          compactHeroMobileImage && '--has-mobile-hero-bg',
          mobileCoverImage && '--has-mobile-hero-cover',
          hasCustomHeader && '--has-custom-header'
        )}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          gap="s500"
          zIndex="2"
          position="relative"
        >
          <Box paddingTop="s800" paddingLeft="s500" paddingRight="s500">
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              gap="s200"
            >
              <Text
                variant="title2XL"
                dataId="HomeHeading"
                colorPath={heroContentColorPath}
              >
                {title}
              </Text>
              <Text variant="bodyM" colorPath={heroContentColorPath}>
                {subtitle}
              </Text>
            </Box>
          </Box>
          <SearchFormMobileWrapper>
            <SearchFormMobile />
          </SearchFormMobileWrapper>
        </Box>
        {isCoBranded && (
          <PoweredByWrapper>
            <PoweredBy />
          </PoweredByWrapper>
        )}
      </Wrapper>
    )

  if (!isMobile && shell.hasAlternativeSearchForm.enabled) {
    return <AlternativeDesktopSearchForm />
  }

  if (isDesktopXs)
    return (
      <Wrapper data-id="HeroSection">
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          gap="s700"
          justifyContent="center"
          zIndex="2"
          position="relative"
          paddingTop="120px"
          paddingBottom="s600"
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="s200"
          >
            <Text align="center" variant="title5XL" dataId="HomeHeading">
              {title}
            </Text>
            <Text align="center" variant="bodyL">
              {subtitle}
            </Text>
          </Box>

          <SearchFormMobileWrapper>
            <SearchFormMobile />
          </SearchFormMobileWrapper>
        </Box>
      </Wrapper>
    )

  return (
    <Wrapper data-id="HeroSection">
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        zIndex="2"
        position="relative"
        height="100%"
        maxWidth="802px"
        margin="0 auto"
        paddingTop="120px"
        paddingBottom="s600"
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          gap="s900"
        >
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="s200"
          >
            <Text align="center" variant="title5XL" dataId="HomeHeading">
              {title}
            </Text>
            <Text align="center" variant="bodyL">
              {subtitle}
            </Text>
          </Box>
          <SearchFormDesktopWrapper>
            <SearchFormDesktop />
          </SearchFormDesktopWrapper>
        </Box>
      </Box>
    </Wrapper>
  )
}
